import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Autocomplete,
  Checkbox,
  CircularProgress
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import DownloadIcon from '@mui/icons-material/Download';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getReturnOrders, getReturnOrderFilterOptions, downloadReturnOrders } from '../services/api';
import { formatDate } from '../utils/tatCalculator';
import { saveAs } from 'file-saver';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const ReturnOrderList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [returnOrders, setReturnOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [filterOptions, setFilterOptions] = useState({ statuses: [], return_types: [] });
  
  // Get today's date and 30 days ago for default range
  const getTodayDate = () => dayjs().format('YYYY-MM-DD');
  const getDefaultStartDate = () => dayjs().subtract(30, 'day').format('YYYY-MM-DD');

  // Initialize filters from URL params
  const [filters, setFilters] = useState({
    date_from: searchParams.get('date_from') || '',
    date_to: searchParams.get('date_to') || '',
    status: searchParams.get('status') || '',
    return_type: searchParams.get('return_type') || ''
  });

  // Date range state for DatePicker
  const [dateRange, setDateRange] = useState([
    filters.date_from ? dayjs(filters.date_from) : null,
    filters.date_to ? dayjs(filters.date_to) : null
  ]);

  const convertFiltersToApiParams = (filterValues = {}) => {
    const apiParams = {};
    Object.keys(filterValues).forEach((key) => {
      const value = filterValues[key];
      if (value !== undefined && value !== null && value !== '') {
        apiParams[key] = value;
      }
    });
    return apiParams;
  };

  // NOTE: API calls disabled - Section is in development
  // Fetch filter options on mount
  // useEffect(() => {
  //   const fetchFilterOptions = async () => {
  //     try {
  //       const response = await getReturnOrderFilterOptions();
  //       setFilterOptions(response.data);
  //     } catch (error) {
  //       console.error('Error fetching filter options:', error);
  //     }
  //   };
  //   fetchFilterOptions();
  // }, []);

  // useEffect(() => {
  //   if (isFirstLoad) {
  //     const hasUrlParams = searchParams.toString().length > 0;
  //     
  //     if (hasUrlParams) {
  //       const urlFilters = {
  //         date_from: searchParams.get('date_from') || '',
  //         date_to: searchParams.get('date_to') || '',
  //         status: searchParams.get('status') || '',
  //         return_type: searchParams.get('return_type') || ''
  //       };
  //       setFilters(urlFilters);
  //       setDateRange([
  //         urlFilters.date_from ? dayjs(urlFilters.date_from) : null,
  //         urlFilters.date_to ? dayjs(urlFilters.date_to) : null
  //       ]);
  //       fetchReturnOrders(urlFilters);
  //     } else {
  //       // Set default date range (last 30 days)
  //       const defaultStart = getDefaultStartDate();
  //       const defaultEnd = getTodayDate();
  //       const initialFilters = {
  //         date_from: defaultStart,
  //         date_to: defaultEnd,
  //         status: '',
  //         return_type: ''
  //       };
  //       setFilters(initialFilters);
  //       setDateRange([dayjs(defaultStart), dayjs(defaultEnd)]);
  //       
  //       const params = new URLSearchParams();
  //       params.set('date_from', defaultStart);
  //       params.set('date_to', defaultEnd);
  //       setSearchParams(params);
  //       
  //       fetchReturnOrders(initialFilters);
  //     }
  //     setIsFirstLoad(false);
  //     return;
  //   }
  //
  //   const urlFilters = {
  //     date_from: searchParams.get('date_from') || '',
  //     date_to: searchParams.get('date_to') || '',
  //     status: searchParams.get('status') || '',
  //     return_type: searchParams.get('return_type') || ''
  //   };
  //   setFilters(urlFilters);
  //   setDateRange([
  //     urlFilters.date_from ? dayjs(urlFilters.date_from) : null,
  //     urlFilters.date_to ? dayjs(urlFilters.date_to) : null
  //   ]);
  //   const apiFilters = convertFiltersToApiParams(urlFilters);
  //   fetchReturnOrders(apiFilters);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [searchParams, isFirstLoad]);

  // NOTE: API calls disabled - Section is in development
  const fetchReturnOrders = async (filterParams = filters) => {
    // Disabled - Section in development
    // setLoading(true);
    // try {
    //   const apiParams = convertFiltersToApiParams(filterParams);
    //   const response = await getReturnOrders(apiParams);
    //   const data = response.data.rows || [];
    //   setReturnOrders(data);
    // } catch (error) {
    //   console.error('Error fetching return orders:', error);
    // } finally {
    //   setLoading(false);
    // }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDateRangeChange = (newValue) => {
    setDateRange(newValue);
    setFilters(prev => ({
      ...prev,
      date_from: newValue[0] ? newValue[0].format('YYYY-MM-DD') : '',
      date_to: newValue[1] ? newValue[1].format('YYYY-MM-DD') : ''
    }));
  };

  const buildSearchParams = (targetFilters) => {
    const params = new URLSearchParams();
    Object.keys(targetFilters).forEach((key) => {
      const value = targetFilters[key];
      if (value) {
        params.set(key, value);
      }
    });
    return params;
  };

  const handleSearch = () => {
    const params = buildSearchParams(filters);
    setSearchParams(params);
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      date_from: '',
      date_to: '',
      status: '',
      return_type: ''
    };
    setFilters(emptyFilters);
    setDateRange([null, null]);
    setSearchParams(new URLSearchParams());
    fetchReturnOrders(emptyFilters);
  };

  // NOTE: API calls disabled - Section is in development
  const handleDownload = async () => {
    // Disabled - Section in development
    // setDownloading(true);
    // try {
    //   const apiParams = convertFiltersToApiParams(filters);
    //   const response = await downloadReturnOrders(apiParams);
    //   const blob = new Blob([response.data], { type: 'text/csv' });
    //   let filename = `return_orders_${new Date().toISOString().split('T')[0]}.csv`;
    //   const contentDisposition = response.headers['content-disposition'];
    //   if (contentDisposition) {
    //     const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    //     if (filenameMatch && filenameMatch[1]) {
    //       filename = filenameMatch[1].replace(/['"]/g, '');
    //     }
    //   }
    //   saveAs(blob, filename);
    // } catch (error) {
    //   console.error('Error downloading file:', error);
    //   alert(error?.message || 'Failed to export. Please try again.');
    // } finally {
    //   setDownloading(false);
    // }
  };

  // Helper function to format empty/null values
  const formatValue = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return '-';
    }
    return value;
  };

  // Get return type color
  const getReturnTypeColor = (type) => {
    switch (type) {
      case 'RTO':
        return { bg: 'rgba(255, 152, 0, 0.1)', border: 'rgba(255, 152, 0, 0.5)' };
      case 'RTV':
        return { bg: 'rgba(33, 150, 243, 0.1)', border: 'rgba(33, 150, 243, 0.5)' };
      case 'EXCHANGE':
        return { bg: 'rgba(156, 39, 176, 0.1)', border: 'rgba(156, 39, 176, 0.5)' };
      default:
        return { bg: 'transparent', border: 'divider' };
    }
  };

  const columns = [
    {
      field: 'return_date',
      headerName: 'Return Date',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => (value ? formatDate(value) : '-')
    },
    {
      field: 'order_name',
      headerName: 'Order Name',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        return React.createElement(Box, {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%'
          }
        },
          React.createElement(Typography, {
            variant: 'body2',
            sx: {
              color: 'text.primary',
              fontWeight: 500
            }
          }, formatValue(params.value))
        );
      }
    },
    {
      field: 'sku',
      headerName: 'SKU',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => formatValue(value)
    },
    {
      field: 'return_id',
      headerName: 'Return ID',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => formatValue(value)
    },
    {
      field: 'return_type',
      headerName: 'Return Type',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const type = formatValue(params.value);
        const colors = getReturnTypeColor(type);
        return React.createElement(Box, {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%'
          }
        },
          React.createElement(Chip, {
            label: type,
            size: 'small',
            sx: {
              backgroundColor: colors.bg,
              border: '1px solid',
              borderColor: colors.border,
              color: 'text.primary',
              fontWeight: 500,
              borderRadius: 2
            }
          })
        );
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const status = formatValue(params.value);
        return React.createElement(Box, {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%'
          }
        },
          React.createElement(Chip, {
            label: status,
            size: 'small',
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              border: '1px solid',
              borderColor: 'divider',
              color: 'text.primary',
              fontWeight: 500,
              borderRadius: 2
            }
          })
        );
      }
    },
    {
      field: 'tracking_number',
      headerName: 'Tracking Number',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => formatValue(value)
    },
    {
      field: 'source_system',
      headerName: 'Source',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => formatValue(value)
    },
    {
      field: 'razorpay_refund_status',
      headerName: 'Refund Status',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const status = formatValue(params.value);
        if (status === '-') return React.createElement(Typography, { variant: 'body2' }, '-');
        
        return React.createElement(Box, {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%'
          }
        },
          React.createElement(Chip, {
            label: status,
            size: 'small',
            sx: {
              backgroundColor: status === 'processed' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(0, 0, 0, 0.05)',
              border: '1px solid',
              borderColor: status === 'processed' ? 'rgba(76, 175, 80, 0.5)' : 'divider',
              color: 'text.primary',
              fontWeight: 500,
              borderRadius: 2
            }
          })
        );
      }
    },
    {
      field: 'razorpay_refund_amount',
      headerName: 'Refund Amount',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value) => {
        if (value === null || value === undefined || value === '') return '-';
        return `₹${Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    },
    {
      field: 'payment_amount',
      headerName: 'Payment Amount',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value) => {
        if (value === null || value === undefined || value === '') return '-';
        return `₹${Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    }
  ];

  // Render filter Autocomplete
  const renderFilterSelect = (label, field, options, placeholder) => {
    return React.createElement(Autocomplete, {
      options: options,
      value: filters[field] || null,
      onChange: (event, newValue) => {
        handleFilterChange(field, newValue || '');
      },
      size: 'small',
      renderInput: (params) => React.createElement(TextField, {
        ...params,
        label: label,
        placeholder: placeholder,
        sx: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: '#ffffff',
            minHeight: '40px',
            '& fieldset': {
              borderColor: 'rgba(0, 0, 0, 0.23)',
              borderWidth: '1px'
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0, 0, 0, 0.87)'
            },
            '&.Mui-focused fieldset': {
              borderColor: 'rgba(0, 0, 0, 0.87)',
              borderWidth: '1px'
            }
          }
        }
      })
    });
  };

  return React.createElement(Container, {
    maxWidth: 'xl',
    sx: {
      px: { xs: 2, sm: 3, md: 4 },
      pt: { xs: 1, sm: 1.5, md: 2 },
      mt: 0,
      position: 'relative'
    }
  },
    // Development Overlay
    React.createElement(Box, {
      sx: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.85)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 3,
        backdropFilter: 'blur(4px)'
      }
    },
      React.createElement(Box, {
        sx: {
          textAlign: 'center',
          p: 4,
          border: '2px dashed',
          borderColor: 'warning.main',
          borderRadius: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          maxWidth: 500
        }
      },
        React.createElement(Typography, {
          variant: 'h4',
          sx: {
            fontWeight: 700,
            color: 'warning.dark',
            mb: 2,
            fontSize: { xs: '1.5rem', sm: '2rem' }
          }
        }, '🚧 In Development'),
        React.createElement(Typography, {
          variant: 'body1',
          sx: {
            color: 'text.secondary',
            mb: 2
          }
        }, 'This section is currently under development.'),
        React.createElement(Typography, {
          variant: 'body2',
          sx: {
            color: 'text.secondary'
          }
        }, 'Features and functionality are not yet finalized.')
      )
    ),
    React.createElement(Box, {
      sx: {
        mb: 4
      }
    },
      React.createElement(Typography, {
        variant: 'h4',
        sx: {
          fontWeight: 600,
          fontSize: { xs: '1.5rem', sm: '2rem' },
          letterSpacing: '-0.01em'
        }
      }, 'Return Orders')
    ),
    React.createElement(Box, {
      sx: {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        p: { xs: 2, sm: 3 },
        mb: 4,
        mt: 1,
        backgroundColor: 'background.paper',
        position: 'relative'
      }
    },
      React.createElement(Typography, {
        variant: 'h6',
        sx: {
          mb: 3,
          fontWeight: 600,
          fontSize: '1.125rem'
        }
      }, 'Filters'),
      React.createElement(Grid, { container: true, spacing: 2 },
        React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 3 },
          renderFilterSelect('Status', 'status', filterOptions.statuses || [], 'Select status...')
        ),
        React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 3 },
          renderFilterSelect('Return Type', 'return_type', filterOptions.return_types || [], 'Select return type...')
        ),
        React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 6 },
          React.createElement(LocalizationProvider, { dateAdapter: AdapterDayjs },
            React.createElement(Box, {
              sx: {
                display: 'flex',
                gap: 1,
                alignItems: 'center'
              }
            },
              React.createElement(DatePicker, {
                label: 'Start Date',
                value: dateRange[0],
                onChange: (newValue) => {
                  const updatedRange = [newValue, dateRange[1]];
                  setDateRange(updatedRange);
                  handleDateRangeChange(updatedRange);
                },
                format: 'DD-MM-YYYY',
                slotProps: {
                  textField: {
                    size: 'small',
                    sx: {
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        minHeight: '40px',
                        '& fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                          borderWidth: '1px'
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.87)'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.87)',
                          borderWidth: '1px'
                        }
                      }
                    }
                  }
                }
              }),
              React.createElement(Typography, {
                sx: { color: 'text.secondary', px: 1 }
              }, 'to'),
              React.createElement(DatePicker, {
                label: 'End Date',
                value: dateRange[1],
                onChange: (newValue) => {
                  const updatedRange = [dateRange[0], newValue];
                  setDateRange(updatedRange);
                  handleDateRangeChange(updatedRange);
                },
                format: 'DD-MM-YYYY',
                slotProps: {
                  textField: {
                    size: 'small',
                    sx: {
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        minHeight: '40px',
                        '& fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                          borderWidth: '1px'
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.87)'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.87)',
                          borderWidth: '1px'
                        }
                      }
                    }
                  }
                }
              })
            )
          )
        )
      ),
      React.createElement(Box, { sx: { mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' } },
        React.createElement(Button, {
          variant: 'outlined',
          startIcon: downloading ? null : React.createElement(DownloadIcon, null),
          onClick: handleDownload,
          disabled: returnOrders.length === 0 || loading || downloading,
          sx: {
            borderColor: 'divider',
            color: 'text.primary',
            textTransform: 'none',
            borderRadius: 2,
            fontWeight: 500,
            height: '40px',
            minWidth: 140,
            '&:hover': {
              borderColor: 'text.primary',
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
            },
            '&:disabled': {
              opacity: 0.5
            }
          }
        }, downloading ? 'Downloading...' : 'Download CSV'),
        downloading && React.createElement(CircularProgress, {
          size: 20,
          sx: { color: 'text.primary' }
        }),
        React.createElement(Button, {
          variant: 'outlined',
          onClick: handleClearFilters,
          sx: {
            minWidth: 120,
            height: '40px',
            borderColor: 'divider',
            color: 'text.primary',
            textTransform: 'none',
            borderRadius: 2,
            fontWeight: 500,
            '&:hover': {
              borderColor: 'text.primary',
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
            }
          }
        }, 'Clear Filters'),
        React.createElement(Button, {
          variant: 'contained',
          onClick: handleSearch,
          sx: {
            minWidth: 120,
            height: '40px',
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            textTransform: 'none',
            borderRadius: 2,
            fontWeight: 500,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
            }
          }
        }, 'Search')
      )
    ),
    React.createElement(Box, {
      sx: {
        height: 600,
        width: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        overflow: 'hidden'
      }
    },
      React.createElement(DataGrid, {
        rows: returnOrders,
        columns: columns,
        loading: loading,
        getRowId: (row, index) => `${row.order_name}-${row.sku}-${row.return_id || index}`,
        pageSizeOptions: [25, 50, 100],
        initialState: {
          pagination: {
            paginationModel: { pageSize: 50 }
          }
        },
        sx: {
          border: 'none',
          '& .MuiDataGrid-row': {
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
            }
          },
          '& .MuiDataGrid-cell': {
            borderColor: 'divider',
          },
          '& .MuiDataGrid-columnHeaders': {
            borderColor: 'divider',
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            fontWeight: 600,
          },
          '& .MuiDataGrid-footerContainer': {
            borderColor: 'divider',
          }
        }
      })
    )
  );
};

export default ReturnOrderList;
